import { Link } from "react-router-dom";
import { LegalLayout, Section, Table, List } from "./LegalLayout";
import { privacyContact } from "@/config/company";
import { revokeConsent } from "@/lib/consent";

/**
 * Cookie policy.
 *
 * Riflette lo stato reale del sito: solo cookie tecnici. Se un domani vengono
 * aggiunte categorie in src/lib/consent.js, questa pagina va aggiornata di
 * conseguenza — è parte dell'adempimento, non un documento decorativo.
 */
export default function CookiePage() {
  return (
    <LegalLayout
      title="Cookie Policy"
      subtitle="Quali cookie usiamo, a cosa servono e come puoi controllarli."
    >
      <Section n={1} title="In breve">
        <p>
          Questo sito usa <strong>solo cookie tecnici</strong>, indispensabili per farlo
          funzionare e per tenerti collegato alla tua area riservata. Non usiamo cookie di
          profilazione, non tracciamo la tua navigazione su altri siti, non registriamo
          le sessioni e non condividiamo dati con circuiti pubblicitari.
        </p>
        <p>
          Per i cookie tecnici la legge non richiede il consenso (art. 122, comma 1,
          D.Lgs 196/2003): è sufficiente questa informativa. Il banner che vedi alla prima
          visita ha quindi valore informativo, non di raccolta del consenso.
        </p>
      </Section>

      <Section n={2} title="Cosa sono i cookie">
        <p>
          I cookie sono piccoli file di testo che un sito salva sul tuo dispositivo. Servono
          a ricordare informazioni tra una pagina e l'altra: per esempio che hai già fatto
          l'accesso. Alcune tecnologie simili (localStorage) funzionano allo stesso modo e
          sono trattate qui insieme ai cookie.
        </p>
      </Section>

      <Section n={3} title="Cookie tecnici utilizzati">
        <Table
          head={["Nome", "Tipo", "Finalità", "Durata"]}
          rows={[
            [
              "access_token",
              "Cookie tecnico (HttpOnly, Secure)",
              "Mantiene la sessione autenticata dopo il login. Senza di esso dovresti reinserire le credenziali a ogni pagina.",
              "24 ore",
            ],
            [
              "refresh_token",
              "Cookie tecnico (HttpOnly, Secure)",
              "Rinnova la sessione scaduta senza chiederti di nuovo la password.",
              "7 giorni",
            ],
            [
              "aura_cookie_consent",
              "localStorage",
              "Ricorda che hai già visto questa informativa, così il banner non ricompare a ogni visita.",
              "6 mesi",
            ],
            [
              "aura_lang",
              "localStorage",
              "Ricorda se preferisci il sito in italiano o in inglese.",
              "Fino a cancellazione manuale",
            ],
          ]}
        />
        <p>
          I cookie contrassegnati come <em>HttpOnly</em> non sono leggibili dal codice
          JavaScript della pagina: è una misura contro il furto di sessione.
        </p>
      </Section>

      <Section n={4} title="Cookie di terze parti">
        <p>
          <strong>Nessuno.</strong> Il sito non incorpora strumenti di analisi statistica,
          pixel pubblicitari, widget social o mappe di terze parti. Anche i caratteri
          tipografici sono ospitati sui nostri server e non su reti di distribuzione
          esterne, così la tua navigazione non viene comunicata a nessun altro fornitore.
        </p>
        <p>
          Alcune immagini possono essere servite da reti di distribuzione contenuti che
          registrano l'indirizzo IP a fini tecnici di consegna: si tratta di trattamento
          necessario alla fruizione della pagina, non di profilazione.
        </p>
      </Section>

      <Section n={5} title="Come gestire i cookie">
        <p>
          Puoi bloccare o cancellare i cookie dalle impostazioni del tuo browser. Tieni
          presente che disattivando i cookie tecnici <strong>non potrai autenticarti</strong>{" "}
          nell'area riservata: sono quelli che tengono viva la sessione.
        </p>
        <List
          items={[
            "Chrome: Impostazioni › Privacy e sicurezza › Cookie e altri dati dei siti",
            "Safari: Impostazioni › Privacy › Gestisci dati dei siti web",
            "Firefox: Impostazioni › Privacy e sicurezza › Cookie e dati dei siti web",
            "Edge: Impostazioni › Cookie e autorizzazioni sito",
          ]}
        />
        <p className="pt-2">
          Puoi anche azzerare la scelta registrata su questo sito e far ricomparire il
          banner:
        </p>
        <button
          type="button"
          onClick={() => {
            revokeConsent();
            window.location.assign("/");
          }}
          data-testid="cookie-policy-revoke"
          className="inline-flex items-center bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
        >
          Reimposta le preferenze cookie
        </button>
      </Section>

      <Section n={6} title="Contatti e aggiornamenti">
        <p>
          Per domande su questa policy scrivi a{" "}
          <a href={`mailto:${privacyContact()}`} className="underline underline-offset-4">
            {privacyContact()}
          </a>
          . Per il quadro completo dei trattamenti consulta la{" "}
          <Link to="/privacy" className="underline underline-offset-4">
            Informativa sulla privacy
          </Link>
          .
        </p>
        <p>
          Se in futuro introdurremo strumenti che richiedono il consenso, questa pagina
          verrà aggiornata e il banner tornerà a chiederti una scelta prima di attivare
          qualsiasi cosa.
        </p>
      </Section>
    </LegalLayout>
  );
}
